import type { ReactNode } from "react";
import { Link } from "@tanstack/react-router";

type Props = {
  eyebrow?: string;
  headline: ReactNode;
  body: string;
  meta?: string;
  cta?: { label: string; to: string };
  secondaryCta?: { label: string; to: string };
  tone?: "brand" | "lavender";
  className?: string;
  /** Opt-in full-bleed photo hero layout (left-aligned, no panel). */
  heroImage?: string;
  /** Opt-in: drop the rounded panel, fill, border, shadow and glows; content stays centred. */
  unboxed?: boolean;
  /** Opt-in: replaces the default headline size/weight classes. */
  headlineClassName?: string;
  /** Opt-in: decorative diagonal ribbon across the top-right corner of the boxed panel. */
  badge?: string;
};

export function MessageBand({
  eyebrow,
  headline,
  body,
  meta,
  cta,
  secondaryCta,
  tone = "lavender",
  className = "py-10 md:py-16",
  heroImage,
  unboxed = false,
  headlineClassName = "text-3xl md:text-5xl",
  badge,
}: Props) {
  const brand = tone === "brand";
  const metaItems = meta
    ?.split("·")
    .map((m) => m.trim())
    .filter(Boolean);

  if (heroImage) {
    return (
      <section className={"relative isolate overflow-hidden bg-background " + className}>
        <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden="true">
          <img
            src={heroImage}
            alt=""
            className="absolute inset-0 h-full w-full object-cover object-center"
          />
          <div
            className="absolute inset-0 md:hidden"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.88) 0%, rgba(255,255,255,0.7) 70%, rgba(255,255,255,0.35) 100%)",
            }}
          />
          <div
            className="absolute inset-0 hidden md:block"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 50%, rgba(255,255,255,0) 100%)",
            }}
          />
          <div
            className="absolute inset-0 hidden md:block"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(255,255,255,0.55) 0%, rgba(255,255,255,0.5) 55%, rgba(255,255,255,0) 75%)",
            }}
          />
          <div
            className="absolute inset-x-0 top-0 h-40"
            style={{
              backgroundImage:
                "linear-gradient(to bottom, rgba(255,255,255,0.75) 0%, rgba(255,255,255,0) 100%)",
            }}
          />
        </div>
        <div className="mx-auto flex min-h-[620px] max-w-6xl items-center px-6">
          <div className="w-full max-w-[640px] text-center text-ink md:w-auto md:text-left">
            {eyebrow && (
              <span className="mb-5 inline-block rounded-full bg-primary/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary">
                {eyebrow}
              </span>
            )}
            <h1 className="text-balance text-3xl font-bold leading-tight md:text-[45px]">{headline}</h1>
            <p className="mt-5 text-[1.1875rem] font-medium leading-relaxed text-muted-foreground md:text-lg">
              {body}
            </p>
            {metaItems && metaItems.length > 0 && (
              <ul className="mt-7 flex flex-wrap justify-center gap-2 md:justify-start">
                {metaItems.map((m) => (
                  <li
                    key={m}
                    className="rounded-full border border-primary/25 bg-background px-4 py-2 text-sm font-medium text-primary"
                  >
                    {m}
                  </li>
                ))}
              </ul>
            )}
            {(cta || secondaryCta) && (
              <div className="mt-8 flex flex-wrap justify-center gap-3 md:justify-start">
                {cta && (
                  <Link
                    to={cta.to}
                    className="inline-flex items-center justify-center rounded-[6px] bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-brand transition hover:-translate-y-0.5 hover:opacity-90"
                  >
                    {cta.label}
                  </Link>
                )}
                {secondaryCta && (
                  <Link
                    to={secondaryCta.to}
                    className="inline-flex items-center justify-center rounded-[6px] border border-primary bg-background/60 px-6 py-3 text-sm font-semibold text-primary transition hover:-translate-y-0.5 hover:bg-primary/5"
                  >
                    {secondaryCta.label}
                  </Link>
                )}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={"bg-background px-4 " + className}>
      <div
        className={
          (unboxed
            ? "relative mx-auto max-w-5xl px-6 text-center text-ink md:px-14"
            : "relative mx-auto max-w-5xl overflow-hidden rounded-3xl px-6 py-12 text-center md:px-14 md:py-16 " +
          (brand
            ? "gradient-brand text-primary-foreground shadow-brand"
            : "border border-primary/20 bg-lavender/50 text-ink shadow-brand"))
        }
      >
        {!unboxed && (<>
        <div
          aria-hidden
          className={
            "pointer-events-none absolute -right-20 -top-20 h-64 w-64 rounded-full blur-3xl " +
            (brand ? "bg-primary-foreground/15" : "bg-primary/10")
          }
        />
        <div
          aria-hidden
          className={
            "pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full blur-3xl " +
            (brand ? "bg-primary-glow/30" : "bg-primary-glow/10")
          }
        />
        </>)}
        {badge && !unboxed && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute right-[-44px] top-[18px] z-10 w-[150px] rotate-45 bg-[#693D94] py-1 text-center text-[13px] font-bold uppercase tracking-[0.12em] text-white shadow-sm md:right-[-48px] md:top-[24px] md:w-[180px] md:py-1.5 md:text-sm"
          >
            {badge}
          </div>
        )}

        <div className="relative">
          {eyebrow && (<span
            className={
              "inline-block rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-wider " +
              (brand
                ? "bg-primary-foreground/15 text-primary-foreground"
                : "bg-primary/10 text-primary")
            }
          >
            {eyebrow}
          </span>)}
          <h2
            className={
              "mx-auto max-w-3xl text-balance leading-tight " +
              (eyebrow || !unboxed ? "mt-5 " : "") +
              headlineClassName
            }
          >
            {headline}
          </h2>
          <p
            className={
              "mx-auto mt-5 max-w-2xl text-[1.1875rem] leading-relaxed md:text-lg " +
              (brand ? "text-primary-foreground/85" : "text-muted-foreground")
            }
          >
            {body}
          </p>

          {metaItems && metaItems.length > 0 && (
            <ul className="mt-7 flex flex-wrap justify-center gap-2">
              {metaItems.map((m) => (
                <li
                  key={m}
                  className={
                    "rounded-full border px-4 py-2 text-sm font-medium " +
                    (brand
                      ? "border-primary-foreground/25 bg-primary-foreground/10 text-primary-foreground"
                      : "border-primary/25 bg-background text-primary")
                  }
                >
                  {m}
                </li>
              ))}
            </ul>
          )}

          {(cta || secondaryCta) && (
            <div className="mt-8 flex flex-wrap justify-center gap-3">
          {cta && (
            <Link
              to={cta.to}
              className={
                "inline-flex min-h-12 items-center justify-center rounded-full px-8 py-3 text-base font-semibold transition hover:-translate-y-0.5 " +
                (brand
                  ? "bg-background text-primary hover:shadow-lg"
                  : "bg-primary text-primary-foreground shadow-brand hover:opacity-90")
              }
            >
              {cta.label}
            </Link>
          )}
          {secondaryCta && (
            <Link
              to={secondaryCta.to}
              className={
                "inline-flex min-h-12 items-center justify-center rounded-full border px-8 py-3 text-base font-semibold transition hover:-translate-y-0.5 " +
                (brand
                  ? "border-primary-foreground/60 text-primary-foreground hover:bg-primary-foreground/10"
                  : "border-primary text-primary hover:bg-primary/5")
              }
            >
              {secondaryCta.label}
            </Link>
          )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
